import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Play, X } from "lucide-react";
import { CATEGORIES, UI, BIO } from "@/data/content";
import { lenisRef } from "@/lib/lenis";

const WorkModal = ({ cat, lang, onClose }) => {
  const t = UI[lang];
  useEffect(() => {
    lenisRef.current?.stop();
    return () => lenisRef.current?.start();
  }, []);
  return (
    <motion.div
      data-testid="work-modal"
      data-lenis-prevent
      className="fixed inset-0 z-[90] overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="fixed inset-0 bg-[#0D0809]/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-3xl mx-auto sm:my-10 bg-[#180F12] border border-[#7E2438]/25"
      >
        <div className="relative h-52 sm:h-64">
          <img src={cat.img} alt={cat.title[lang]} style={cat.pos ? { objectPosition: cat.pos } : undefined} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#180F12] via-[#180F12]/40 to-transparent" />
          <button
            data-testid="work-modal-close"
            onClick={onClose}
            aria-label={t.close}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0D0809]/70 border border-[#7E2438]/40 flex items-center justify-center text-[#F4EFEA] hover:bg-[#7E2438] transition-colors"
          >
            <X size={16} />
          </button>
          <div className="absolute bottom-4 left-6">
            <h3 className="font-serif text-2xl sm:text-3xl text-[#F4EFEA]">{cat.title[lang]}</h3>
            <p className="text-sm text-[#A89A9E] mt-1">{cat.subtitle[lang]}</p>
          </div>
        </div>
        <div className="px-6 py-8 divide-y divide-[#7E2438]/15">
          {cat.items.map((item, i) => (
            <div key={i} data-testid={`work-item-${cat.id}-${i}`} className="py-5 first:pt-0">
              <div className="flex items-baseline gap-4">
                {item.year && <span className="font-mono text-xs tracking-[0.2em] text-[#B03A52] shrink-0">{item.year}</span>}
                <h4 className="font-serif text-xl text-[#F4EFEA]">{item.title[lang]}</h4>
                <a
                  data-testid={`work-link-${cat.id}-${i}`}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border border-[#7E2438]/40 px-4 py-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-[#B03A52] transition-colors duration-300 hover:bg-[#7E2438] hover:text-[#F4EFEA]"
                >
                  <Play size={11} /> {t.watch}
                </a>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#A89A9E]">{item.excerpt[lang]}</p>
            </div>
          ))}
        </div>
        <p data-testid="work-modal-bio" className="px-6 py-5 border-t border-[#7E2438]/25 text-xs text-[#A89A9E] leading-relaxed">
          {BIO[lang]}
        </p>
      </motion.div>
    </motion.div>
  );
};

export default function WorkGrid({ lang }) {
  const t = UI[lang];
  const [open, setOpen] = useState(null);
  const active = CATEGORIES.find((c) => c.id === open);

  return (
    <section id="works" data-testid="works-section" className="relative px-6 sm:px-12 md:px-20 py-28">
      <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#B03A52]">{t.worksEyebrow}</span>
      <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif text-[#F4EFEA]">{t.worksTitle}</h2>
      <p className="mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-[#A89A9E]">{t.worksDesc}</p>
      <div className="mt-14 grid sm:grid-cols-2 gap-5">
        {CATEGORIES.map((cat, i) => (
          <motion.button
            key={cat.id}
            data-testid={`work-card-${cat.id}`}
            onClick={() => setOpen(cat.id)}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="group relative aspect-[4/3] sm:aspect-[4/5] overflow-hidden text-left border border-[#7E2438]/25"
          >
            <img
              src={cat.img}
              alt={cat.title[lang]}
              style={cat.pos ? { objectPosition: cat.pos } : undefined}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0809] via-[#0D0809]/30 to-transparent" />
            <div className="absolute inset-0 bg-[#7E2438]/0 group-hover:bg-[#7E2438]/25 transition-colors duration-500" />
            <div className="absolute top-4 right-4 w-10 h-10 rounded-full border border-[#F4EFEA]/30 flex items-center justify-center text-[#F4EFEA] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <ArrowUpRight size={16} />
            </div>
            <div className="absolute bottom-0 inset-x-0 p-6">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#B03A52]">
                {cat.count} {t.pieces}
              </div>
              <h3 className="mt-2 font-serif text-xl sm:text-2xl text-[#F4EFEA]">{cat.title[lang]}</h3>
              <p className="mt-1 text-sm text-[#A89A9E]">{cat.subtitle[lang]}</p>
            </div>
          </motion.button>
        ))}
      </div>
      <AnimatePresence>
        {active && <WorkModal cat={active} lang={lang} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}
